import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getProductDetailPath,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@b2b/ui-components";
import { Textarea } from "@b2b/ui-components/shadcn/ui/Textarea";
import { sellerService } from "@b2b/services";
import { Mail, Reply, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, X, RefreshCw } from "lucide-react";

interface EnquiryItem {
  id: string;
  buyerName: string;
  buyerEmail: string;
  subject: string;
  message: string;
  productId: string | null;
  status: "PENDING" | "RESPONDED" | "CLOSED";
  response: string | null;
  respondedAt: string | null;
  createdAt: string;
  product?: { id: string; name: string } | null;
}

const DEFAULT_LIMIT = 10;

export default function SellerEnquiriesPage() {
  const [inquiries, setInquiries] = useState<EnquiryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<EnquiryItem | null>(null);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const activeFilters: string[] = [];
  if (searchTerm) activeFilters.push(`Search: "${searchTerm}"`);
  if (statusFilter !== "all") activeFilters.push(`Status: ${statusFilter.toLowerCase()}`);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const statusParam =
        appliedStatusFilter === "all"
          ? undefined
          : (appliedStatusFilter as "pending" | "responded" | "closed");
      const response = await sellerService.getStorefrontInquiries(
        statusParam,
        page,
        limit,
        appliedSearchTerm || undefined
      );
      setInquiries(Array.isArray(response.inquiries) ? response.inquiries : []);
      setTotal(response.total ?? 0);
    } catch (err: any) {
      console.error("Failed to fetch enquiries:", err);
      showToast("error", err?.response?.data?.message || "Failed to load enquiries");
      setInquiries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [appliedSearchTerm, appliedStatusFilter, page, limit]);

  /** Commit current search box + status to the query (used by Search button and status filter). */
  const commitFilters = (search: string, status: string) => {
    setAppliedSearchTerm(search);
    setAppliedStatusFilter(status);
    setPage(1);
  };

  const handleSearch = () => {
    commitFilters(searchTerm, statusFilter);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setAppliedSearchTerm("");
    setAppliedStatusFilter("all");
    setPage(1);
  };

  const handleRemoveFilter = (filter: string) => {
    if (filter.startsWith("Search:")) {
      setSearchTerm("");
      setAppliedSearchTerm("");
    } else if (filter.startsWith("Status:")) {
      setStatusFilter("all");
      setAppliedStatusFilter("all");
    }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const openReplyModal = (inquiry: EnquiryItem) => {
    setReplyingTo(inquiry);
    setReplyText(inquiry.response || "");
    setReplyModalOpen(true);
  };

  const handleReplySubmit = async () => {
    if (!replyingTo) return;
    setSubmitting(true);
    try {
      await sellerService.updateInquiryStatus(
        replyingTo.id,
        "RESPONDED",
        replyText.trim() || undefined
      );
      showToast("success", "Response sent successfully");
      setReplyModalOpen(false);
      setReplyingTo(null);
      setReplyText("");
      fetchInquiries();
    } catch (err: any) {
      showToast("error", err?.response?.data?.message || "Failed to send response");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: EnquiryItem["status"]) => {
    const config: Record<
      EnquiryItem["status"],
      { label: string; className: string }
    > = {
      PENDING: { label: "Pending", className: "bg-amber-100 text-amber-800" },
      RESPONDED: { label: "Responded", className: "bg-green-100 text-green-800" },
      CLOSED: { label: "Closed", className: "bg-gray-100 text-gray-700" },
    };
    const { label, className } = config[status] ?? config.PENDING;
    return <Badge variant="secondary" className={className}>{label}</Badge>;
  };

  return (
    <div className="w-full min-w-0 max-w-none py-6 space-y-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-foreground">Enquiries</h1>
          <p className="text-sm text-muted-foreground">
            View and respond to product enquiries from buyers
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleClearSearch}
          disabled={!searchTerm && statusFilter === "all" && !appliedSearchTerm && appliedStatusFilter === "all"}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Clear Filters
        </Button>
      </div>

      <Card className="p-4">
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search by subject, message, or buyer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                commitFilters(searchTerm, v);
              }}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem
                  value="all"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  All Status
                </SelectItem>
                <SelectItem
                  value="PENDING"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Pending
                </SelectItem>
                <SelectItem
                  value="RESPONDED"
                  className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                >
                  Responded
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="border border-gray-300"
            >
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
          </div>
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilters.map((filter, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => handleRemoveFilter(filter)}
                >
                  {filter}
                  <X className="h-3 w-3" />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>
                {appliedSearchTerm || appliedStatusFilter !== "all"
                  ? "No enquiries found matching your search criteria."
                  : "No enquiries found."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {inquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="border rounded-lg overflow-hidden bg-white"
                >
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50/50"
                    onClick={() =>
                      setExpandedId(expandedId === inquiry.id ? null : inquiry.id)
                    }
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            {inquiry.subject}
                          </span>
                          {getStatusBadge(inquiry.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          From: <span className="font-medium">{inquiry.buyerName}</span>
                          {inquiry.buyerEmail && (
                            <span className="text-gray-500"> ({inquiry.buyerEmail})</span>
                          )}
                        </p>
                        {inquiry.product && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            Product:{" "}
                            <Link
                              to={getProductDetailPath(inquiry.product)}
                              className="text-orange-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {inquiry.product.name}
                            </Link>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {new Date(inquiry.createdAt).toLocaleDateString()}
                        </span>
                        {expandedId === inquiry.id ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </div>
                    {expandedId === inquiry.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {inquiry.message}
                        </p>
                        {inquiry.response && (
                          <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                            <span className="font-medium text-gray-600">Your response: </span>
                            <span className="whitespace-pre-wrap">{inquiry.response}</span>
                          </div>
                        )}
                        {inquiry.status === "PENDING" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReplyModal(inquiry);
                            }}
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            Reply
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Showing {start} to {end} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Reply to enquiry</DialogTitle>
          </DialogHeader>
          {replyingTo && (
            <>
              <Textarea
                placeholder="Enter your response..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setReplyModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReplySubmit}
                  disabled={submitting}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {submitting ? "Sending..." : "Send response"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
